import { useCallback, useEffect, useState } from "react";

const DISMISS_KEY = "chin:install-hint-dismissed:v1";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function isStandalone(): boolean {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as { standalone?: boolean }).standalone === true)
  );
}

function isIos(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function hintDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Instalación de la PWA: en Android/desktop captura el prompt nativo
 * (Chrome no siempre lo sugiere solo); en iOS no existe el prompt, así que
 * expone una pista con los pasos manuales.
 */
export function usePwaInstall() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [iosHintVisible, setIosHintVisible] = useState(() => isIos() && !isStandalone() && !hintDismissed());

  useEffect(function capturePwaInstallPrompt() {
    function handleBeforeInstall(event: Event) {
      event.preventDefault();
      setPromptEvent(event as BeforeInstallPromptEvent);
    }
    function handleInstalled() {
      setPromptEvent(null);
      setIosHintVisible(false);
    }

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleInstalled);
    return function releasePwaInstallPrompt() {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!promptEvent) return;
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === "accepted") setPromptEvent(null);
  }, [promptEvent]);

  const dismissIosHint = useCallback(() => {
    setIosHintVisible(false);
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // Sin storage la pista vuelve la próxima visita; no es un problema.
    }
  }, []);

  return {
    canInstall: promptEvent !== null && !isStandalone(),
    iosHintVisible,
    install,
    dismissIosHint,
  };
}
